import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listActiveTestimonials } from "@/lib/cms/testimonials.functions";
import {
  TESTIMONIAL_FALLBACKS,
  type TestimonialItem,
} from "@/lib/testimonials-defaults";

export type UseTestimonialsResult = {
  testimonials: TestimonialItem[];
  isFallback: boolean;
  isEmpty: boolean;
  isLoading: boolean;
};

export function useTestimonials(): UseTestimonialsResult {
  const fetchFn = useServerFn(listActiveTestimonials);
  const { data, isLoading } = useQuery({
    queryKey: ["testimonials", "public"],
    queryFn: () => fetchFn(),
    staleTime: 60_000,
  });

  if (data === null || data === undefined) {
    if (isLoading) {
      return { testimonials: [], isFallback: false, isEmpty: false, isLoading: true };
    }
    // Database-error path: null → bundled fallbacks.
    return {
      testimonials: TESTIMONIAL_FALLBACKS,
      isFallback: true,
      isEmpty: false,
      isLoading: false,
    };
  }

  if (data.length === 0) {
    // Honest empty state — no fake demo content.
    return { testimonials: [], isFallback: false, isEmpty: true, isLoading: false };
  }

  const testimonials: TestimonialItem[] = data.map((r) => ({
    id: r.id,
    customerName: r.customerName,
    designation: r.designation,
    company: r.company,
    testimonial: r.testimonial,
    rating: Math.max(1, Math.min(5, Math.round(r.rating))),
    avatarUrl: r.avatarUrl,
    altText: r.altText,
    isDemo: r.isDemo,
  }));

  return { testimonials, isFallback: false, isEmpty: false, isLoading: false };
}